/* eslint-disable no-magic-numbers */
import {
  Euler,
  MathUtils,
  Raycaster,
  Vector2,
  Vector3,
  Web3DMeshMaterial,
  Plane,
  Face,
} from '@technology/web3d-plugin-threejs';
import { QuantizedMeshSettings } from './config/qm-settings';
import { LRUCache } from 'lru-cache';
import { calculateGpuMemoryCacheSize } from '@technology/web3d/dist/esnext/Helpers/caching.js';
import { Api } from '@technology/web3d/dist/esnext/Api.js';
import {
  Geometry,
  GeometryModel,
  IIntersection,
  Mesh,
  NavigationPickableModel,
  PickableModel,
  type GeometryType,
  type SnapType,
  type Web3DEventName,
} from '@technology/web3d';
const SNAP_TYPE_FACE = 0;
// GeometryType.MESH (0). Inlined to avoid importing the const enum value (isolatedModules).
const GEOMETRY_TYPE_MESH = 0;
// Web3DEventName is an ambient const enum, which cannot be accessed with isolatedModules.
const NAVIGATION_START_EVENT =
  'navigationstart' as Web3DEventName.NavigationStart;
const NAVIGATION_END_EVENT = 'navigationend' as Web3DEventName.NavigationEnd;
import { Caster } from '@technology/web3d/dist/esnext/Picker/Caster.js';
import proj4 from 'proj4';
import { QmDecoder } from './processing/qm-decoder';
import { QuantizedMeshData } from './types/quantized-mesh-data';
import { approximateQuantizedMeshDataSize } from './processing/qm-factory';
import { QMNode } from './qm-node';
import { TileCache } from './qm-tile-cache';
import { ObjectPool } from './object-pool';
import {
  PromiseQueue,
  type PromiseQueueDebugSnapshot,
} from './processing/promise-queue';
import { TextureImage } from './types/texture-image';
import { GenericModel } from '@technology/web3d/dist/esnext/GenericModel';
import { getLogger } from '@trimble/logging';
import { MeshCommitQueue } from './processing/mesh-commit-queue';
import type { TileMesh } from './qm-node';
import { UpdateScheduler } from './processing/update-scheduler';
import { getTileMinMax } from './mesh-utils';
import { computeTerrainBounds } from './terrain-bounds';
import {
  claimUncovered,
  limitConcurrency,
  piecesToSamples,
  ProfilePiece,
  sectionTile,
  TerrainProfileSample,
  tileSpansAlongLine,
} from './processing/qm-profile';

const REFERENCE_SYSTEM = proj4.Proj('WGS84');
// Profile fetches bypass the render queue (where they'd be evicted or preempted
// by on-screen tiles). The tile API is HTTP/2, so there is no per-host
// connection cap; this only bounds the load put on the tile backend, shared
// by every path being profiled.
const PROFILE_FETCH_CONCURRENCY = 16;
// Re-sectioning on every arriving tile would redo the whole path per tile.
const PROFILE_PROGRESS_INTERVAL_MS = 250;
const QM_DATA_QUEUE_CONCURRENCY = 10;
const QM_DATA_QUEUE_MAX_QUEUED_LENGTH = 100;
const TEXTURE_QUEUE_CONCURRENCY = 8;
const TEXTURE_QUEUE_MAX_QUEUED_LENGTH = 100;
const QUEUE_DEBUG_SAMPLE_INTERVAL_MS = 500;
const QUEUE_DEBUG_LOG_WINDOW_MS = 2_000;
const MISSING_CHILD_COVERAGE_SCREEN_SIZE_FACTOR = 0.5;
const MISSING_CHILD_COVERAGE_EDGE_NDC_MARGIN = 0.1;
const LARGE_LOW_RES_FALLBACK_SCREEN_SIZE_FACTOR = 4;

type QueueDebugTextureSnapshot = PromiseQueueDebugSnapshot & {
  id: string;
};

type QueueDebugThroughputStats = {
  id: string;
  type: string;
  completedRequestsInWindow: number;
  averageCompletedRequestsPerSecond: number;
};

type QueueDebugThroughput = {
  qmDataQueue: QueueDebugThroughputStats;
  textureQueues: QueueDebugThroughputStats[];
};

type QueueDebugTaskSnapshot = PromiseQueueDebugSnapshot['queuedTasks'][number];

type TileQueueDebugMatch = {
  queueId: string;
  queueType: string;
  key: string;
  lifecycle: 'queued' | 'active';
  position: number | null;
  ageMs: number;
  priority: number;
  aborted: boolean;
  abortReason: QueueDebugTaskSnapshot['abortReason'];
};

type VisibleNonRenderableTileDebugSnapshot = {
  total: number;
  unmatched: number;
  byState: Record<string, number>;
  tiles: Array<{
    tileId: string;
    parentTileId: string | undefined;
    state: string;
    screenSizePx: number;
    priority: number;
    hasMesh: boolean;
    textureId: string | undefined;
    blockingReason: 'noMesh' | 'stateNotRenderable';
    queues: {
      qmData: TileQueueDebugMatch[];
      textures: TileQueueDebugMatch[];
    };
  }>;
};

type ClickTileDebugInfo = {
  tileId: string;
  parentTileId: string | undefined;
  state: string;
  isVisibleInFrame: boolean;
  lastTouchedFrame: number;
  hasMesh: boolean;
  textureId: string | undefined;
  screenSizePx: number;
  priority: number;
};

type ClickTileDescendantDebugInfo = ClickTileDebugInfo & {
  depth: number;
  queues: {
    qmData: TileQueueDebugMatch[];
    textures: TileQueueDebugMatch[];
  };
};

type ClickTileSubtreeDebugSnapshot = {
  frame: number;
  textureId: string;
  clickedTile: ClickTileDebugInfo;
  descendants: ClickTileDescendantDebugInfo[];
  summary: {
    totalDescendants: number;
    visibleDescendants: number;
    queuedOrActiveDescendants: number;
    unmatchedVisibleLoadingDescendants: number;
    byState: Record<string, number>;
  };
};

type QueueDebugStateSnapshot = {
  timestampMs: number;
  frame: number;
  textureId: string;
  renderedTiles: number;
  staleRenderedTiles: number;
  qmDataQueue: PromiseQueueDebugSnapshot;
  textureQueues: QueueDebugTextureSnapshot[];
  rootTileCounts: Array<{
    tileId: string;
    tiles: number;
  }>;
};

// Create a reusable Euler instance for camera constraint calculations
const euler = new Euler(0, 0, 0, 'ZYX');
const maxPitch = Math.PI / 2; // Prevent looking above the XY plane (horizon).
const minPitch = 0; // Allow looking straight down.

export class QuantizedMeshModel
  extends GenericModel
  implements PickableModel, NavigationPickableModel, GeometryModel
{
  private disposed = false;
  private readonly tileTreeRoots: Array<QMNode>;

  private readonly textureCache!: LRUCache<number, ImageBitmap>;
  private readonly qmDataCache: LRUCache<number, QuantizedMeshData>;
  private readonly profileTileRequests = new Map<
    number,
    Promise<QuantizedMeshData | undefined>
  >();
  /** Shared by every concurrent {@link sampleProfile}. */
  private readonly withProfileFetchSlot = limitConcurrency(
    PROFILE_FETCH_CONCURRENCY
  );
  private readonly qmDecoder!: QmDecoder;

  private readonly intervalHandle: ReturnType<typeof setInterval> | null = null;
  private readonly queueDebugLogIntervalHandle: ReturnType<
    typeof setInterval
  > | null = null;
  private readonly queueDebugStateHistory: QueueDebugStateSnapshot[] = [];

  private readonly raycaster = new Raycaster();
  private readonly mouse = new Vector2();
  private readonly projectedTileCorner = new Vector3();
  // TODO: Implement wrapping so that long running sessions do not overflow the frameNumber
  private frameNumber = 0;
  // private frameStart = 0;
  private readonly tileCache: TileCache;
  private renderInProgress = false;
  private readonly objectPool = new ObjectPool();
  // This flag is used to ensure that some housekeeping tasks are done
  // only once, after the first tiles have been rendered.
  private firstTilesRendered = false;
  private isNavigating = false;
  private queuesNeedReprioritizing = false;
  // Once real terrain has been loaded, map mode keeps using it (the shader
  // flattens it), so later switches back to terrain need no reload.
  private hasShownTerrain = false;

  private readonly qmDataPromiseQueue: PromiseQueue<QuantizedMeshData>;
  private readonly textureQueues: Map<string, PromiseQueue<TextureImage>> =
    new Map();
  private readonly meshCommitQueue = new MeshCommitQueue<
    TileMesh | undefined
  >();
  private readonly updateScheduler = new UpdateScheduler(() => {
    this.renderScene();
  });

  private readonly constrainCameraMovementFn =
    this.constrainCameraMovement.bind(this);
  private readonly navigationStartedFn = (): void => {
    this.isNavigating = true;
    this.updateScheduler.request();
  };
  private readonly navigationEndedFn = (): void => {
    this.isNavigating = false;
    this.updateScheduler.request();
  };
  private readonly cameraChangedFn = (): void => {
    this.updateScheduler.request();
  };

  private constrainCameraMovement(): void {
    euler.setFromQuaternion(this.api.camera!.quaternion);
    if (euler.x < minPitch || euler.x > maxPitch) {
      euler.x = MathUtils.clamp(euler.x, minPitch, maxPitch);
      this.api.camera!.quaternion.setFromEuler(euler);
    }

    const cameraHeight = this.api.camera!.position.z;
    const baseHeight = this.qmDecoder.baseHeight ?? 0.0;
    if (cameraHeight < baseHeight + 10) {
      this.api.camera!.position.z = baseHeight + 10;
    }
  }

  private mapModeBasePlane: Plane | undefined;

  constructor(
    private readonly mapSettings: QuantizedMeshSettings,
    private readonly api: Api,
    decodeWorkerUrl: string
  ) {
    super(mapSettings.options.modelId, api);
    if (!this.api.camera) {
      throw new Error('QuantizedMeshSMModel: api.camera is not defined');
    }
    this.qmDataPromiseQueue = new PromiseQueue<QuantizedMeshData>(
      mapSettings.options.meshQueueConcurrency ?? QM_DATA_QUEUE_CONCURRENCY,
      'qm-data',
      QM_DATA_QUEUE_MAX_QUEUED_LENGTH
    );

    if (this.mapSettings.options.constrainCamera) {
      api.camera?.subscribe(this.constrainCameraMovementFn);
    }
    api.camera.subscribe(this.cameraChangedFn);
    api.eventDispatcher?.subscribe(
      NAVIGATION_START_EVENT,
      this.navigationStartedFn
    );
    api.eventDispatcher?.subscribe(
      NAVIGATION_END_EVENT,
      this.navigationEndedFn
    );

    this.qmDecoder = new QmDecoder(
      decodeWorkerUrl,
      this.mapSettings.tileFetcher.fetcher,
      this.mapSettings.options.tileOptions.flatZoomLevels,
      this.mapSettings.options.meshSettings.baseHeight,
      this.mapSettings.options.geometryWorkerCount
    );

    if (this.mapSettings.tileOptions.initialTile) {
      this.qmDecoder.initialTile = this.mapSettings.tileOptions.initialTile;
    }
    this.hasShownTerrain = this.mapSettings.displayMode === 'terrain';
    this.qmDecoder.flatMeshMode = this.shouldUseFlatMeshes();

    this.mapSettings.updateTextureId();

    this.textureCache = this.createTextureCache(this.mapSettings.imageSize);
    this.qmDataCache = this.createQMDataCache();
    this.tileTreeRoots = [];
    this.tileCache = new TileCache();

    // Create a tileTreeRoot for each tile adjacent to the root tile.
    // This is necessary to ensure that the sites that straddle the border
    // of the root tile can also be viewed in their entirety.
    const rootCoordinate = this.mapSettings.options.tileOptions.rootCoordinate;
    const maxCoordinateAtZoom =
      Math.pow(2, this.mapSettings.options.tileOptions.rootZoomLevel) - 1;

    for (let x = -1; x <= 1; x++) {
      for (let y = -1; y <= 1; y++) {
        const coordinate = new Vector2(
          rootCoordinate.x + x,
          rootCoordinate.y + y
        );

        // Wrap coordinates to ensure they stay within the valid range.
        // This would cause the tiles to wrap around the world if the edge is reached.
        if (coordinate.x < 0) coordinate.x = maxCoordinateAtZoom;
        if (coordinate.y < 0) coordinate.y = maxCoordinateAtZoom;
        if (coordinate.x > maxCoordinateAtZoom) coordinate.x = 0;
        if (coordinate.y > maxCoordinateAtZoom) coordinate.y = 0;

        const tileTreeRoot = new QMNode(
          api,
          mapSettings,
          this.root,
          this.mapSettings.options.tileOptions.rootZoomLevel,
          coordinate,
          null,
          this.textureCache,
          this.qmDataCache,
          this.qmDecoder,
          this.objectPool,
          this.textureQueues,
          this.qmDataPromiseQueue,
          this.meshCommitQueue,
          (delayMs) => this.updateScheduler.request(delayMs),
          undefined,
          this.mapSettings.options.textureQueueConcurrency ??
            TEXTURE_QUEUE_CONCURRENCY,
          TEXTURE_QUEUE_MAX_QUEUED_LENGTH
        );
        this.tileTreeRoots.push(tileTreeRoot);
      }
    }

    this.updateScheduler.request();
    // This is used for stepping through the rendering process while debugging.
    // window.addEventListener('mouseup', (evt) => {
    //   if (evt.button === 2) {
    //     console.log('Setting renderScene on right mouse up');
    //     this.renderScene();
    //   }
    // });

    if (this.mapSettings.debug) {
      window.addEventListener('click', (e) => {
        this.debugHandleMouseClick(e);
      });
      this.recordQueueDebugState();
      this.intervalHandle = setInterval(() => {
        this.recordQueueDebugState();
      }, QUEUE_DEBUG_SAMPLE_INTERVAL_MS);
      this.queueDebugLogIntervalHandle = setInterval(() => {
        this.logRollingQueueDebugState();
      }, QUEUE_DEBUG_LOG_WINDOW_MS);
    }
  }

  private renderedTiles = new Set<QMNode>();

  /**
   * Listeners notified whenever the set of tiles attached to {@link root}
   * changes (a tile is added or removed in {@link renderScene}). Consumers
   * such as the section profile pipeline use this to re-extract geometry
   * once new LOD has loaded in. Consumers should debounce their reaction —
   * during continuous panning this can fire many times per second.
   */
  private readonly tilesChangedListeners = new Set<() => void>();

  /**
   * Subscribe to be notified when the rendered tile set of this model
   * changes (LOD shifts, new tiles streaming in, tiles dropping out of
   * view). Returns an unsubscribe function.
   */
  public onTilesChanged(callback: () => void): () => void {
    this.tilesChangedListeners.add(callback);
    return () => {
      this.tilesChangedListeners.delete(callback);
    };
  }

  private notifyTilesChanged(): void {
    for (const cb of this.tilesChangedListeners) {
      try {
        cb();
      } catch (e) {
        getLogger().error(
          '[QuantizedMeshModel] onTilesChanged listener threw',
          e
        );
      }
    }
  }

  private createQueueDebugStateSnapshot(
    timestampMs: number
  ): QueueDebugStateSnapshot {
    const staleRenderedTiles = Array.from(this.renderedTiles).filter(
      (tile) => tile.textureId !== this.mapSettings.textureId
    );
    const rootTileCounts = this.tileTreeRoots.map((root) => ({
      tileId: root.tileId,
      tiles: root.countTilesInTree(),
    }));

    return {
      timestampMs,
      frame: this.frameNumber,
      textureId: this.mapSettings.textureId,
      renderedTiles: this.renderedTiles.size,
      staleRenderedTiles: staleRenderedTiles.length,
      qmDataQueue: this.qmDataPromiseQueue.getDebugSnapshot(
        QUEUE_DEBUG_LOG_WINDOW_MS
      ),
      textureQueues: Array.from(this.textureQueues.entries()).map(
        ([id, queue]) => ({
          id,
          ...queue.getDebugSnapshot(QUEUE_DEBUG_LOG_WINDOW_MS),
        })
      ),
      rootTileCounts,
    };
  }

  private recordQueueDebugState(): void {
    const now = performance.now();
    this.pruneQueueDebugHistory(now);
    this.queueDebugStateHistory.push(this.createQueueDebugStateSnapshot(now));
    this.pruneQueueDebugHistory(now);
  }

  private pruneQueueDebugHistory(now: number): void {
    const minTimestamp = now - QUEUE_DEBUG_LOG_WINDOW_MS;
    while (
      this.queueDebugStateHistory.length > 0 &&
      this.queueDebugStateHistory[0].timestampMs < minTimestamp
    ) {
      this.queueDebugStateHistory.shift();
    }
  }

  private getQueueDebugThroughput(): QueueDebugThroughput {
    const latestSnapshot =
      this.queueDebugStateHistory[this.queueDebugStateHistory.length - 1];

    return {
      qmDataQueue: this.getQueueDebugThroughputStats(
        'qm-data',
        latestSnapshot?.qmDataQueue
      ),
      textureQueues: latestSnapshot
        ? latestSnapshot.textureQueues.map((latestTextureQueue) =>
            this.getQueueDebugThroughputStats(
              latestTextureQueue.id,
              latestTextureQueue
            )
          )
        : [],
    };
  }

  private getQueueDebugThroughputStats(
    id: string,
    snapshot: PromiseQueueDebugSnapshot | undefined
  ): QueueDebugThroughputStats {
    return {
      id,
      type: snapshot?.type ?? id,
      completedRequestsInWindow: snapshot?.completedInWindow ?? 0,
      averageCompletedRequestsPerSecond:
        snapshot?.averageCompletedRequestsPerSecond ?? 0,
    };
  }

  private logRollingQueueDebugState(): void {
    const now = performance.now();
    this.recordQueueDebugState();
    this.pruneQueueDebugHistory(now);
    const latestSnapshot =
      this.queueDebugStateHistory[this.queueDebugStateHistory.length - 1];

    getLogger().info('[QuantizedMeshModel] rolling queue state', {
      windowMs: QUEUE_DEBUG_LOG_WINDOW_MS,
      sampleIntervalMs: QUEUE_DEBUG_SAMPLE_INTERVAL_MS,
      samples: this.queueDebugStateHistory.length,
      queueThroughput: this.getQueueDebugThroughput(),
      visibleNonRenderableTiles:
        this.getVisibleNonRenderableTileDebugSnapshot(latestSnapshot),
      history: this.queueDebugStateHistory,
    });
  }

  private getVisibleNonRenderableTileDebugSnapshot(
    queueSnapshot: QueueDebugStateSnapshot | undefined
  ): VisibleNonRenderableTileDebugSnapshot {
    const tiles: VisibleNonRenderableTileDebugSnapshot['tiles'] = [];
    const byState: Record<string, number> = {};

    for (const root of this.tileTreeRoots) {
      root.traverseAll((node) => {
        if (!this.isVisibleNonRenderableTile(node)) {
          return;
        }

        const queues = this.getTileQueueDebugMatches(node, queueSnapshot);
        const stateCount = byState[node.state] ?? 0;
        byState[node.state] = stateCount + 1;
        tiles.push({
          tileId: node.tileId,
          parentTileId: node.parent?.tileId,
          state: node.state,
          screenSizePx: node.calculateTileScreenSize(),
          priority: node.tilePriority(),
          hasMesh: node.mesh != null,
          textureId: node.textureId,
          blockingReason: node.mesh ? 'stateNotRenderable' : 'noMesh',
          queues,
        });
      });
    }

    return {
      total: tiles.length,
      unmatched: tiles.filter(
        (tile) =>
          tile.queues.qmData.length === 0 && tile.queues.textures.length === 0
      ).length,
      byState,
      tiles,
    };
  }

  private isVisibleNonRenderableTile(node: QMNode): boolean {
    if (!node.isVisibleInFrame) {
      return false;
    }

    if (!node.mesh) {
      return true;
    }

    return node.state === 'start' || node.state === 'loading';
  }

  private getTileQueueDebugMatches(
    node: QMNode,
    queueSnapshot: QueueDebugStateSnapshot | undefined
  ): VisibleNonRenderableTileDebugSnapshot['tiles'][number]['queues'] {
    const tileKeyFragment = this.getTileQueueKeyFragment(node);
    return {
      qmData: queueSnapshot
        ? this.getQueueDebugMatches(
            'qm-data',
            queueSnapshot.qmDataQueue,
            tileKeyFragment
          )
        : [],
      textures: queueSnapshot
        ? queueSnapshot.textureQueues.flatMap((textureQueue) =>
            this.getQueueDebugMatches(
              textureQueue.id,
              textureQueue,
              tileKeyFragment
            )
          )
        : [],
    };
  }

  private getTileQueueKeyFragment(node: QMNode): string {
    return node.queueKeyFragment;
  }

  private getQueueDebugMatches(
    queueId: string,
    queueSnapshot: PromiseQueueDebugSnapshot,
    tileKeyFragment: string
  ): TileQueueDebugMatch[] {
    return [
      ...this.getQueueTaskDebugMatches(
        queueId,
        queueSnapshot.type,
        'queued',
        queueSnapshot.queuedTasks,
        tileKeyFragment
      ),
      ...this.getQueueTaskDebugMatches(
        queueId,
        queueSnapshot.type,
        'active',
        queueSnapshot.activeTasks,
        tileKeyFragment
      ),
    ];
  }

  private getQueueTaskDebugMatches(
    queueId: string,
    queueType: string,
    lifecycle: 'queued' | 'active',
    tasks: QueueDebugTaskSnapshot[],
    tileKeyFragment: string
  ): TileQueueDebugMatch[] {
    return tasks
      .filter((task) => task.key.includes(tileKeyFragment))
      .map((task) => ({
        queueId,
        queueType,
        key: task.key,
        lifecycle,
        position: task.position,
        ageMs: task.ageMs,
        priority: task.priority,
        aborted: task.aborted,
        abortReason: task.abortReason,
      }));
  }

  private getClickTileSubtreeDebugSnapshot(
    clickedNode: QMNode,
    queueSnapshot: QueueDebugStateSnapshot
  ): ClickTileSubtreeDebugSnapshot {
    const descendants: ClickTileDescendantDebugInfo[] = [];
    const byState: Record<string, number> = {};

    clickedNode.traverseDescendantsWithDepth((node, depth) => {
      const queues = this.getTileQueueDebugMatches(node, queueSnapshot);
      const stateCount = byState[node.state] ?? 0;
      byState[node.state] = stateCount + 1;
      descendants.push({
        ...this.getClickTileDebugInfo(node),
        depth,
        queues,
      });
    });

    return {
      frame: this.frameNumber,
      textureId: this.mapSettings.textureId,
      clickedTile: this.getClickTileDebugInfo(clickedNode),
      descendants,
      summary: {
        totalDescendants: descendants.length,
        visibleDescendants: descendants.filter((node) => node.isVisibleInFrame)
          .length,
        queuedOrActiveDescendants: descendants.filter(
          (node) =>
            node.queues.qmData.length > 0 || node.queues.textures.length > 0
        ).length,
        unmatchedVisibleLoadingDescendants: descendants.filter(
          (node) =>
            node.isVisibleInFrame &&
            (node.state === 'loading' || node.state === 'start') &&
            node.queues.qmData.length === 0 &&
            node.queues.textures.length === 0
        ).length,
        byState,
      },
    };
  }

  private getClickTileDebugInfo(node: QMNode): ClickTileDebugInfo {
    return {
      tileId: node.tileId,
      parentTileId: node.parent?.tileId,
      state: node.state,
      isVisibleInFrame: node.isVisibleInFrame,
      lastTouchedFrame: node.lastTouchedFrame,
      hasMesh: node.mesh != null,
      textureId: node.textureId,
      screenSizePx: node.calculateTileScreenSize(),
      priority: node.tilePriority(),
    };
  }

  private renderScene(): void {
    if (this.renderInProgress) {
      console.log('======== render in progress, skipping this frame ========');
      return;
    }

    if (this.disposed) {
      return;
    }

    // console.log('textureQueue length', this.textureFetcherPromiseQueue.length);
    // console.log('qmQueue length', this.textureFetcherPromiseQueue.length);

    this.renderInProgress = true;
    // this.frameStart = performance.now();

    this.tileTreeRoots.forEach((t) => {
      t.update(
        this.frameNumber,
        this.api.camera!.frustum,
        this.firstTilesRendered,
        this.isNavigating
      );
    });

    // for (const root of this.tileTreeRoots) {
    //   if (root.countTilesInTree() > 30) {
    //     console.log('active tree', root);
    //   }
    // }

    const renderableTiles: QMNode[] = [];
    this.tileTreeRoots.forEach((t) => {
      this.collectRenderableTiles(t, renderableTiles);
    });
    if (this.queuesNeedReprioritizing) {
      this.queuesNeedReprioritizing = false;
      this.qmDataPromiseQueue.reprioritize();
      this.textureQueues.forEach((queue) => {
        queue.reprioritize();
      });
    }
    let promotedReadyTile = false;
    for (const tile of renderableTiles) {
      if (tile.state === 'ready') {
        tile.transitionTo('rendered');
        promotedReadyTile = true;
      }
      tile.ensureLatestTexture(true);
      if (!this.tileCache.get(tile.tileId)) {
        this.tileCache.add(tile);
      }
    }

    const newRenderableSet = new Set(renderableTiles);

    const tilesToAdd: QMNode[] = [];
    for (const tile of renderableTiles) {
      if (!this.renderedTiles.has(tile)) {
        tilesToAdd.push(tile);
      }
    }

    const tilesToRemove: QMNode[] = [];
    for (const tile of this.renderedTiles) {
      if (!newRenderableSet.has(tile)) {
        tilesToRemove.push(tile);
      }
    }

    this.renderedTiles = newRenderableSet;

    // console.log(
    //   'Renderable tiles this frame:',
    //   renderableTiles.length,
    //   renderableTiles
    // );

    tilesToAdd.forEach((t) => {
      this.root.add(t.mesh!);
    });

    if (!this.firstTilesRendered && this.renderedTiles.size > 0) {
      this.firstTilesRendered = true;
      this.handleInitialRender();
    }

    tilesToRemove.forEach((t) => {
      this.root.remove(t.mesh!);
    });

    const renderedTilesChanged =
      tilesToAdd.length > 0 || tilesToRemove.length > 0;
    if (renderedTilesChanged) {
      this.notifyTilesChanged();
      this.updateBoundingBox();
    }

    this.tileCache.prune(this.frameNumber);
    this.frameNumber = (this.frameNumber + 1) % 16_777_216;
    if (promotedReadyTile) {
      this.updateScheduler.request();
    }

    this.renderInProgress = false;
    this.objectPool.reset();
    // console.log(
    //   '======== frame done ========',
    //   performance.now() - this.frameStart
    // );
    // const root = this.tileTreeRoots.find((r) => r.isVisibleInFrame);

    // console.log('Total tiles in tree:', root?.countTilesInTree());
    if (renderedTilesChanged) {
      this.redraw();
    }
  }

  private redraw(): void {
    // Web3D v5 resizes render targets to 0x0 while its container is hidden or
    // not laid out, leaving an incomplete framebuffer for the next draw.
    const container = this.api.container;
    if (container && container.clientWidth > 0 && container.clientHeight > 0) {
      this.api.renderingManager?.redraw();
    }
  }

  private addBasePlane(elevation: number): void {
    this.mapModeBasePlane = new Plane(new Vector3(0, 0, 1), 0);
    this.mapModeBasePlane.setFromNormalAndCoplanarPoint(
      new Vector3(0, 0, 1),
      new Vector3(0, 0, elevation)
    );
  }

  private handleInitialRender(): void {
    this.updateBoundingBox();
    const evt = new CustomEvent('terrainrendered', {
      bubbles: true,
      composed: true,
      cancelable: false,
      detail: {
        baseHeight: this.qmDecoder.baseHeight,
        initialTile: this.qmDecoder.initialTile,
      },
    });
    this.api.eventDispatcher?.dispatch(evt);
    this.addBasePlane(this.qmDecoder.baseHeight ?? 0.0);
    this.updateBoundingBox();
  }

  private collectRenderableTiles(node: QMNode, collection: QMNode[]): void {
    void this.collectRenderableCoverage(node, collection);
  }

  private collectRenderableCoverage(
    node: QMNode,
    collection: QMNode[]
  ): boolean {
    if (!node.isVisibleInFrame) {
      // Prefetched siblings build meshes without being drawn; the cache must
      // still see them so it can reclaim them once they go unused.
      if (node.mesh && !this.tileCache.get(node.tileId)) {
        this.tileCache.add(node);
      }
      return true;
    }

    if (
      (node.state === 'refining' || node.state === 'refined') &&
      node.children?.length
    ) {
      const childTiles: QMNode[] = [];
      let childrenCovered = true;
      const shouldBoostBlockingChildren =
        this.shouldBoostChildrenForLargeLowResolutionFallback(node);

      for (const child of node.children) {
        const childCovered = this.collectRenderableCoverage(child, childTiles);
        if (
          !childCovered &&
          (this.isStaleNewcomer(child) ||
            !this.canAllowMissingChildCoverageGap(child))
        ) {
          if (shouldBoostBlockingChildren) {
            child.boostFallbackRefinementPrioritySubtree();
            this.queuesNeedReprioritizing = true;
          }
          childrenCovered = false;
        }
      }

      if (
        childrenCovered &&
        childTiles.length > 0 &&
        !this.holdForChildTextures(node, childTiles)
      ) {
        collection.push(...childTiles);
        return true;
      }
    }

    return this.collectFallbackTile(node, collection);
  }

  /**
   * Swapping a drawn tile that shows every layer for children that only have
   * the required ones would make overlays such as orthos vanish and reappear,
   * so the tile stays until its children have caught up. Children already
   * drawn are kept and repainted in place instead, so an overlay change does
   * not drop the view back to a coarser tile.
   */
  private holdForChildTextures(node: QMNode, childTiles: QMNode[]): boolean {
    if (
      !node.mesh ||
      !this.renderedTiles.has(node) ||
      !node.hasLatestTexture()
    ) {
      return false;
    }

    const staleTiles = childTiles.filter((tile) => !tile.hasLatestTexture());
    if (staleTiles.length === 0) {
      return false;
    }

    staleTiles.forEach((tile) => tile.ensureLatestTexture(true));
    this.queuesNeedReprioritizing = true;
    return true;
  }

  private shouldBoostChildrenForLargeLowResolutionFallback(
    node: QMNode
  ): boolean {
    if (!node.mesh || !node.isVisibleInFrame) {
      return false;
    }

    const minBoostScreenSize =
      this.mapSettings.imageSize *
      devicePixelRatio *
      LARGE_LOW_RES_FALLBACK_SCREEN_SIZE_FACTOR;

    return node.calculateTileScreenSize() >= minBoostScreenSize;
  }

  private canAllowMissingChildCoverageGap(child: QMNode): boolean {
    if (!child.isVisibleInFrame) {
      return true;
    }

    const maxAllowedScreenSize =
      this.mapSettings.imageSize *
      devicePixelRatio *
      MISSING_CHILD_COVERAGE_SCREEN_SIZE_FACTOR;

    if (child.calculateTileScreenSize() <= maxAllowedScreenSize) {
      return true;
    }

    const edgeVisibleScreenSize =
      this.calculateEdgeVisibleTileScreenSize(child);

    return (
      edgeVisibleScreenSize !== undefined &&
      edgeVisibleScreenSize <= maxAllowedScreenSize
    );
  }

  private calculateEdgeVisibleTileScreenSize(
    child: QMNode
  ): number | undefined {
    const camera = this.api.camera;
    const container = this.api.container;
    if (
      !camera ||
      !container ||
      container.clientWidth <= 0 ||
      container.clientHeight <= 0
    ) {
      return undefined;
    }

    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    const box = child.box;

    for (let cornerIndex = 0; cornerIndex < 8; cornerIndex++) {
      this.projectedTileCorner
        .set(
          cornerIndex & 1 ? box.max.x : box.min.x,
          cornerIndex & 2 ? box.max.y : box.min.y,
          cornerIndex & 4 ? box.max.z : box.min.z
        )
        .project(camera);

      if (
        !Number.isFinite(this.projectedTileCorner.x) ||
        !Number.isFinite(this.projectedTileCorner.y)
      ) {
        return undefined;
      }

      minX = Math.min(minX, this.projectedTileCorner.x);
      minY = Math.min(minY, this.projectedTileCorner.y);
      maxX = Math.max(maxX, this.projectedTileCorner.x);
      maxY = Math.max(maxY, this.projectedTileCorner.y);
    }

    const isNearScreenEdge =
      minX <= -1 + MISSING_CHILD_COVERAGE_EDGE_NDC_MARGIN ||
      maxX >= 1 - MISSING_CHILD_COVERAGE_EDGE_NDC_MARGIN ||
      minY <= -1 + MISSING_CHILD_COVERAGE_EDGE_NDC_MARGIN ||
      maxY >= 1 - MISSING_CHILD_COVERAGE_EDGE_NDC_MARGIN;

    if (!isNearScreenEdge) {
      return undefined;
    }

    const visibleWidthPixels =
      ((MathUtils.clamp(maxX, -1, 1) - MathUtils.clamp(minX, -1, 1)) / 2) *
      container.clientWidth *
      devicePixelRatio;
    const visibleHeightPixels =
      ((MathUtils.clamp(maxY, -1, 1) - MathUtils.clamp(minY, -1, 1)) / 2) *
      container.clientHeight *
      devicePixelRatio;

    return Math.max(visibleWidthPixels, visibleHeightPixels);
  }

  private collectFallbackTile(node: QMNode, collection: QMNode[]): boolean {
    if (!node.mesh) {
      return false;
    }

    if (this.isStaleNewcomer(node)) {
      node.ensureLatestTexture(true);
      this.queuesNeedReprioritizing = true;
      return false;
    }

    collection.push(node);
    return true;
  }

  /**
   * Repaints only reach tiles on screen, so a tile that was off screen when
   * the layers changed comes back with the old ones (e.g. the previous
   * basemap when zooming out). Its parent stands in until it has repainted.
   * Without an up-to-date parent the tile is drawn anyway: old layers are
   * better than a hole.
   */
  private isStaleNewcomer(node: QMNode): boolean {
    return (
      node.mesh != null &&
      !this.renderedTiles.has(node) &&
      !node.hasLatestTexture() &&
      node.parent?.mesh != null &&
      node.parent.hasLatestTexture()
    );
  }

  /**
   * Caps how deep visible tiles refine, or removes the cap with `null`.
   * Tiles already deeper than the cap fall back to their parent.
   */
  public setRefinementMaxZoomLevel(level: number | null): void {
    this.mapSettings.refinementMaxZoomLevel = level;
    this.updateScheduler.request();
  }

  public repaint(): void {
    this.mapSettings.updateTextureId();

    this.textureQueues.forEach((queue) => {
      queue.clear();
    });

    const promises: Array<Promise<void>> = [];

    this.tileTreeRoots.forEach((r) => {
      r.traverseAll((node: QMNode) => {
        if (node.mesh && node.isVisibleInFrame) {
          const repaintPromise = node.repaint(this.renderedTiles.has(node));
          promises.push(repaintPromise);
        }
      });
    });

    void Promise.all(promises)
      .then(() => {
        getLogger().debug('Repaint complete');
      })
      .catch((err: unknown) => {
        getLogger().error('Repaint error', err);
      });
  }

  /** Set the base height for the terrain.
   * @param height The new base height, or null to use the default value passed in the
   * settings object.
   * @param options.remesh Whether to rebuild the terrain tiles after changing the base height. If true, disposes and resets all terrain nodes, causing meshes to regenerate.
   * If false, just the height will be updated and the existing meshes will be used.
   */
  public setBaseHeight(
    height: number | null,
    options: { readonly remesh?: boolean } = {}
  ): void {
    if (this.qmDecoder.baseHeight === height) {
      return;
    }
    if (height === null) {
      this.qmDecoder.baseHeight =
        this.mapSettings.options.meshSettings.baseHeight;
    } else {
      this.qmDecoder.baseHeight = height;
    }

    const newHeight = this.qmDecoder.baseHeight ?? 0.0;

    this.mapModeBasePlane?.setFromNormalAndCoplanarPoint(
      new Vector3(0, 0, 1),
      new Vector3(0, 0, newHeight)
    );
    // Skip remesh when the caller already invalidated/reset tiles (e.g. filter
    // Apply: invalidateTerrainData then update height for the next fetch).
    if (options.remesh !== false) {
      this.resetTerrainNodes();
    } else {
      this.redraw();
    }
  }

  /**
   * Drop cached mesh data and force visible tiles to refetch. Used when the
   * terrain filter changes but the model instance is kept (same modelId).
   */
  public invalidateTerrainData(): void {
    // Bump first so in-flight tasks that finish after clear() cannot write
    // results keyed only by tile coordinates into the emptied cache.
    this.qmDecoder.bumpMeshDataGeneration();
    this.qmDataCache.clear();
    this.profileTileRequests.clear();
    this.qmDataPromiseQueue.clear();
    // Flat zoom levels reuse initialTile; drop the previous filter's buffer
    // (restore a caller-supplied blank tile when options provide one).
    this.qmDecoder.initialTile =
      this.mapSettings.tileOptions.initialTile ?? null;
    this.resetTerrainNodes();
  }

  private resetTerrainNodes(): void {
    this.tileTreeRoots.forEach((r) => {
      r.traverseAll((node: QMNode) => {
        node.dispose();
        node.lastTouchedFrame = 0;
        node.state = 'start';
      });
    });
    this.renderedTiles.clear();
    this.updateScheduler.request();
    this.redraw();
  }

  public setOpacity(opacity: number): void {
    for (const r of this.tileTreeRoots) {
      r.traverseAll((node) => {
        this.mapSettings.options.meshSettings.opacity = opacity;
        const material = node.mesh?.material as Web3DMeshMaterial | undefined;
        if (!node.mesh || !material) {
          return;
        }
        material.transparent = opacity < 1;
        material.opacity = opacity;
      });
    }
    this.redraw();
  }

  public async setDisplayMode(mode: 'terrain' | 'map'): Promise<void> {
    if (mode === this.mapSettings.displayMode) {
      return;
    }

    this.mapSettings.displayMode = mode;
    if (mode === 'terrain') {
      this.hasShownTerrain = true;
    }
    const wasFlat = this.qmDecoder.flatMeshMode;
    this.qmDecoder.flatMeshMode = this.shouldUseFlatMeshes();
    if (wasFlat && !this.qmDecoder.flatMeshMode) {
      this.reloadFlatTiles();
    }
    await this.setDisplayModeSmoothly(mode, 300, performance.now());
  }

  private shouldUseFlatMeshes(): boolean {
    return (
      this.mapSettings.options.meshSettings.flatMeshInMapMode === true &&
      this.mapSettings.displayMode === 'map' &&
      !this.hasShownTerrain
    );
  }

  /**
   * Tiles built while flat have no real heights, so they are rebuilt as
   * terrain. Flat data is never cached, so the mesh cache only holds real
   * terrain (e.g. from profiles) and is kept.
   */
  private reloadFlatTiles(): void {
    // Flat requests still in flight must not create meshes after the reset.
    this.qmDecoder.bumpMeshDataGeneration();
    this.resetTerrainNodes();
  }

  private async setDisplayModeSmoothly(
    mode: 'terrain' | 'map',
    duration: number,
    start: number
  ): Promise<void> {
    const now = performance.now();
    const progress = (now - start) / duration;

    this.tileTreeRoots.forEach((rootNode) => {
      rootNode.traverseAll((node: QMNode) => {
        if (node.mesh) {
          node.setDisplayMode(mode, Math.min(progress, 1));
        }
      });
    });
    this.redraw();
    const framePromise = new Promise<void>((resolve) => {
      this.api.renderingManager?.addAnimationFrameListener(() => {
        resolve();
        void this.api.renderingManager?.waitForRender();
      });
    });
    await framePromise;
    if (progress <= 1) {
      await this.setDisplayModeSmoothly(mode, duration, start);
    }
  }

  public override dispose(): void {
    this.disposed = true;
    super.dispose();
    this.qmDataPromiseQueue.clear();
    this.textureQueues.forEach((queue) => queue.clear());
    this.textureQueues.clear();
    this.meshCommitQueue.dispose();
    this.updateScheduler.dispose();
    this.tileTreeRoots.forEach((r) => {
      r.dispose();
    });
    this.tileCache.clear();
    this.textureCache.clear();
    this.qmDataCache.clear();
    this.profileTileRequests.clear();
    this.qmDecoder.dispose();
    if (this.intervalHandle) {
      clearInterval(this.intervalHandle);
    }
    if (this.queueDebugLogIntervalHandle) {
      clearInterval(this.queueDebugLogIntervalHandle);
    }
    window.removeEventListener('click', this.debugHandleMouseClick);
    if (this.mapSettings.options.constrainCamera) {
      this.api.camera?.unsubscribe(this.constrainCameraMovementFn);
    }
    this.api.camera.unsubscribe(this.cameraChangedFn);
    this.api.eventDispatcher?.unsubscribe(
      NAVIGATION_START_EVENT,
      this.navigationStartedFn
    );
    this.api.eventDispatcher?.unsubscribe(
      NAVIGATION_END_EVENT,
      this.navigationEndedFn
    );
  }

  public async initializeWorkerTgl(
    csib: ArrayBuffer,
    shiftGrids?: Map<string, ArrayBuffer>
  ): Promise<void> {
    await this.qmDecoder.initializeWorkerTgl(csib, shiftGrids);
  }

  private createTextureCache(imageSize: number): LRUCache<number, ImageBitmap> {
    const maxSize = Math.max(
      this.api.settingsDispatcher!.settings.maxGpuMemoryCache ??
        calculateGpuMemoryCacheSize(),
      1
    );
    return new LRUCache<number, ImageBitmap>({
      maxSize: maxSize * 2,
      sizeCalculation: (_t) => {
        return imageSize * imageSize * 3;
      },
    });
  }

  private createQMDataCache(): LRUCache<number, QuantizedMeshData> {
    return new LRUCache<number, QuantizedMeshData>({
      maxSize: 1024 * 1024 * 200,
      sizeCalculation: (t: QuantizedMeshData) => {
        return approximateQuantizedMeshDataSize(t);
      },
    });
  }

  public raycastHeight(raycaster: Raycaster): number | null {
    const intersections = raycaster.intersectObject(this.root, true);
    if (intersections.length > 0) {
      if (this.mapSettings.displayMode === 'map') {
        return this.qmDecoder.baseHeight ?? 0.0;
      } else {
        return intersections[0].point.z;
      }
    }

    // If no intersection is found, return null to indicate that the raycast did not hit the terrain
    return null;
  }

  /**
   * Profiles the terrain along a WGS84 polyline after fetching the most
   * detailed tiles that fit in `maxTiles`, independent of the LOD on screen;
   * finer tiles already in the cache still win where they cover the path.
   * `result[i]` covers `lonLats[i] → lonLats[i + 1]`. Waits for the terrain
   * to render first, as that sets the no-data height. `undefined` if the
   * polyline can't be projected onto the map. `onProgress` receives the
   * profile so far, at most every {@link PROFILE_PROGRESS_INTERVAL_MS}, while
   * tiles are still arriving.
   */
  public async sampleProfile(
    lonLats: readonly { readonly lon: number; readonly lat: number }[],
    options: {
      readonly maxTiles: number;
      readonly signal: AbortSignal;
      onProgress?(samples: TerrainProfileSample[][]): void;
    }
  ): Promise<TerrainProfileSample[][] | undefined> {
    const noDataHeight = await this.whenNoDataHeightKnown(options.signal);
    const lines = this.toMapLines(lonLats);
    if (!lines) {
      return undefined;
    }
    const bounds = this.mapSettings.mapProjection.bounds;
    const tilesAt = (zoom: number): { x: number; y: number }[] => {
      const unique = new Map<string, { x: number; y: number }>();
      for (const [a, b] of lines) {
        for (const { x, y } of tileSpansAlongLine(a, b, zoom, bounds)) {
          unique.set(`${x}/${y}`, { x, y });
        }
      }
      return [...unique.values()];
    };

    let zoom = this.mapSettings.maxZoomLevel;
    let pending = tilesAt(zoom);
    while (
      zoom > this.mapSettings.tileOptions.rootZoomLevel &&
      pending.length > options.maxTiles
    ) {
      zoom--;
      pending = tilesAt(zoom);
    }
    // Popped from the end, so this fetches from the start of the path.
    pending.reverse();

    // Held here as well as in the cache, which may evict them before we section.
    const fetched = new Map<number, QuantizedMeshData>();
    let progressTimer: ReturnType<typeof setTimeout> | undefined;
    const scheduleProgress = (): void => {
      if (!options.onProgress || progressTimer !== undefined) {
        return;
      }
      progressTimer = setTimeout(() => {
        progressTimer = undefined;
        if (!options.signal.aborted) {
          options.onProgress?.(
            this.sectionFinestTiles(lines, noDataHeight, fetched)
          );
        }
      }, PROFILE_PROGRESS_INTERVAL_MS);
    };
    const fetchPendingTiles = async (): Promise<void> => {
      try {
        for (let tile = pending.pop(); tile; tile = pending.pop()) {
          const { x, y } = tile;
          const data = await this.withProfileFetchSlot(() => {
            options.signal.throwIfAborted();
            return this.fetchProfileTile(x, y, zoom);
          });
          if (data) {
            fetched.set(QMNode.cacheKey(x, y, zoom), data);
            scheduleProgress();
          }
        }
      } catch (error) {
        // Invalidation doesn't abort the signal; stop the other workers too.
        pending.length = 0;
        throw error;
      }
    };
    try {
      await Promise.all(
        Array.from(
          { length: Math.min(PROFILE_FETCH_CONCURRENCY, pending.length) },
          fetchPendingTiles
        )
      );
    } finally {
      clearTimeout(progressTimer);
    }
    options.signal.throwIfAborted();
    return this.sectionFinestTiles(lines, noDataHeight, fetched);
  }

  /**
   * Profiles a WGS84 polyline from tiles already decoded (on screen now or
   * earlier), using the finest one available along each part of it, so the
   * result never coarsens when the view zooms out. Same shape as
   * {@link sampleProfile}; `undefined` until the terrain has rendered.
   */
  public sampleCachedProfile(
    lonLats: readonly { readonly lon: number; readonly lat: number }[]
  ): TerrainProfileSample[][] | undefined {
    const noDataHeight = this.qmDecoder.initialBaseHeight;
    if (noDataHeight === null) {
      return undefined;
    }
    const lines = this.toMapLines(lonLats);
    return lines && this.sectionFinestTiles(lines, noDataHeight, new Map());
  }

  /**
   * The first decoded tile sets the no-data height, so profile tiles (which
   * hold real heights) must not be fetched before the terrain has rendered.
   */
  private whenNoDataHeightKnown(signal: AbortSignal): Promise<number> {
    signal.throwIfAborted();
    const known = this.qmDecoder.initialBaseHeight;
    if (known !== null) {
      return Promise.resolve(known);
    }
    return new Promise((resolve, reject) => {
      const stop = (): void => {
        unsubscribe();
        signal.removeEventListener('abort', onAbort);
      };
      const onAbort = (): void => {
        stop();
        reject(signal.reason);
      };
      const unsubscribe = this.onTilesChanged(() => {
        const height = this.qmDecoder.initialBaseHeight;
        if (height !== null) {
          stop();
          resolve(height);
        }
      });
      signal.addEventListener('abort', onAbort, { once: true });
    });
  }

  private toMapLines(
    lonLats: readonly { readonly lon: number; readonly lat: number }[]
  ): (readonly [Vector2, Vector2])[] | undefined {
    const points: Vector2[] = [];
    for (const { lon, lat } of lonLats) {
      // proj4 yields null when a datum shift fails for the point.
      const p = proj4.transform(
        REFERENCE_SYSTEM,
        this.mapSettings.mapProj,
        new Vector2(lon, lat),
        false
      );
      if (!p) {
        return undefined;
      }
      points.push(new Vector2(p.x, p.y));
    }
    return points.slice(1).map((end, i) => [points[i], end] as const);
  }

  // ponytail: walks every zoom level's tile spans along each line (cheap
  // arithmetic, but O(path length / finest tile size)); index cached tiles
  // per zoom if very long paths ever make this show up in profiles.
  private sectionFinestTiles(
    lines: readonly (readonly [Vector2, Vector2])[],
    noDataHeight: number,
    tiles: ReadonlyMap<number, QuantizedMeshData>
  ): TerrainProfileSample[][] {
    const bounds = this.mapSettings.mapProjection.bounds;
    return lines.map(([a, b]) => {
      const covered: [number, number][] = [];
      const pieces: ProfilePiece[] = [];
      for (
        let zoom = this.mapSettings.maxZoomLevel;
        zoom >= this.mapSettings.tileOptions.rootZoomLevel;
        zoom--
      ) {
        for (const span of tileSpansAlongLine(a, b, zoom, bounds)) {
          const key = QMNode.cacheKey(span.x, span.y, zoom);
          const data = tiles.get(key) ?? this.qmDataCache.peek(key);
          if (!data) {
            continue;
          }
          const { min, max } = getTileMinMax(
            new Vector2(span.x, span.y),
            zoom,
            bounds
          );
          for (const [t0, t1] of claimUncovered(covered, span.t0, span.t1)) {
            pieces.push(
              ...sectionTile(
                data,
                min,
                max,
                a,
                b,
                { ...span, t0, t1 },
                noDataHeight
              )
            );
          }
        }
      }
      return piecesToSamples(pieces);
    });
  }

  /**
   * Tile downloads aren't tied to the profile that asked for them: a profile
   * cancelled by a path edit still lets in-flight tiles land in the cache for
   * the next request, and concurrent profiles share one download per tile.
   */
  private fetchProfileTile(
    x: number,
    y: number,
    z: number
  ): Promise<QuantizedMeshData | undefined> {
    const key = QMNode.cacheKey(x, y, z);
    const cached = this.qmDataCache.get(key);
    if (cached) {
      return Promise.resolve(cached);
    }
    const inFlight = this.profileTileRequests.get(key);
    if (inFlight) {
      return inFlight;
    }
    const request = this.downloadProfileTile(x, y, z, key).finally(() => {
      if (this.profileTileRequests.get(key) === request) {
        this.profileTileRequests.delete(key);
      }
    });
    this.profileTileRequests.set(key, request);
    return request;
  }

  private async downloadProfileTile(
    x: number,
    y: number,
    z: number,
    key: number
  ): Promise<QuantizedMeshData | undefined> {
    const generation = this.qmDecoder.meshDataGeneration;
    try {
      const data = await this.qmDecoder.fetchAndDecode(
        { x, y, z },
        new AbortController(),
        false
      );
      if (generation === this.qmDecoder.meshDataGeneration) {
        this.qmDataCache.set(key, data);
      }
      return data;
    } catch (error) {
      // Terrain data was invalidated (e.g. filter Apply) mid-download.
      if (error instanceof Error && error.name === 'AbortError') {
        throw error;
      }
      getLogger().warn(
        `Terrain profile tile ${z}/${x}/${y} failed; left as a gap`,
        error
      );
      return undefined;
    }
  }

  public override _setSelection(_ids: number[]): void {
    // No-op, selection not supported
  }

  //@ts-expect-error Interface does not include undefined return, but this is valid
  public pickNavigation(caster: Caster): IIntersection {
    const intersects: IIntersection[] = [];
    let point: Vector3 | null = null;
    let normal: Vector3 | undefined = undefined;
    let face: Face | undefined | null = undefined;
    if (this.mapSettings.displayMode === 'map') {
      const pointOnPlane = new Vector3();
      if (!this.mapModeBasePlane) {
        point = null;
      } else {
        point = caster.ray.intersectPlane(this.mapModeBasePlane, pointOnPlane);
        normal = this.mapModeBasePlane.normal.clone();
      }
    } else {
      const terrainIntersection = caster.intersectObject(this.root);
      if (terrainIntersection.length > 0) {
        point = terrainIntersection[0].point;
        normal =
          terrainIntersection[0].face?.normal.clone() || new Vector3(0, 0, 1);
        face = terrainIntersection[0].face;
      }
    }

    if (point) {
      intersects.push({
        point,
        distance: point.distanceTo(caster.camera.position),
        normal,
        id: 0,
        face: face ?? undefined, // Let null values be coerced to undefined to satisfy the IIntersection interface
        object: this.root,
        model: this,
        caster: caster,
        snapType: SNAP_TYPE_FACE,
      });
    }

    if (intersects.length > 0) {
      const intersection = intersects[0];
      if (intersection.face) {
        const localNormal = intersection.face.normal;
        const object = intersection.object;
        const worldNormal = new Vector3();
        worldNormal.copy(localNormal).transformDirection(object.matrixWorld);
        intersection.normal = worldNormal;
      }

      return intersection;
    }
  }

  public override async pick(caster: Caster): Promise<IIntersection> {
    return this.pickNavigation(caster);
  }

  public override async pickSnapped(
    caster: Caster,
    snapTypes: SnapType[]
  ): Promise<IIntersection[]> {
    if (
      !snapTypes.includes(SNAP_TYPE_FACE as SnapType) ||
      !caster.ray.intersectsBox(this.getModelBoundingBox())
    ) {
      // @ts-expect-error Interface does not include undefined return, but this is valid
      return;
    }

    const intersections = this._intersectModel(caster).map((intersection) => {
      return intersection;
    });

    if (this.mapSettings.displayMode === 'map' && this.mapModeBasePlane) {
      const pointOnPlane = new Vector3();
      if (caster.ray.intersectPlane(this.mapModeBasePlane, pointOnPlane)) {
        const result: IIntersection[] = [];

        if (snapTypes.includes(SNAP_TYPE_FACE as SnapType)) {
          result.push({
            point: pointOnPlane.clone(),
            distance: pointOnPlane.distanceTo(caster.camera.position),
            normal: this.mapModeBasePlane.normal.clone(),
            id: 0,
            object: this.root,
            model: this,
            caster: caster,
            snapType: SNAP_TYPE_FACE,
          } as IIntersection);
        }
        return result;
      }
    }

    return Promise.resolve(intersections);
  }

  public override async areaPick(
    _caster: Caster,
    _containedOnly: boolean
  ): Promise<IIntersection> {
    // @ts-expect-error Interface does not include undefined return, but this is valid
    return;
  }

  public cartographicToPosition(p: Vector2, fromProjection?: string): Vector2 {
    return this.mapSettings.projectPoint(
      p,
      fromProjection ? proj4.Proj(fromProjection) : REFERENCE_SYSTEM,
      this.mapSettings.sceneProj,
      new Vector2()
    );
  }

  public positionToCartographic(p: Vector2, toProjection?: string): Vector2 {
    return this.mapSettings.projectPoint(
      p,
      this.mapSettings.sceneProj,
      toProjection ? proj4.Proj(toProjection) : REFERENCE_SYSTEM,
      new Vector2()
    );
  }

  private readonly debugHandleMouseClick = (event: MouseEvent): void => {
    try {
      // Convert screen coordinates to Three.js normalized device coordinates (-1 to +1)
      this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

      this.raycaster.setFromCamera(this.mouse, this.api.camera!);
      const intersects = this.raycaster.intersectObject(this.root);

      if (intersects.length > 0) {
        const userData = intersects[0].object.userData as { node?: unknown };
        if (!(userData.node instanceof QMNode)) {
          getLogger().warn(
            '[QuantizedMeshModel] clicked tile did not contain a QMNode',
            intersects[0].object.userData
          );
          return;
        }

        const now = performance.now();
        const queueSnapshot = this.createQueueDebugStateSnapshot(now);
        getLogger().info(
          '[QuantizedMeshModel] clicked tile subtree',
          this.getClickTileSubtreeDebugSnapshot(userData.node, queueSnapshot)
        );
      } else {
        getLogger().info(
          '[QuantizedMeshModel] no tile intersected at click position'
        );
      }
    } catch (error) {
      getLogger().error('[QuantizedMeshModel] error during raycasting', error);
    }
  };

  private updateBoundingBox(): void {
    this._boundingBox.next(
      computeTerrainBounds(
        this.tileTreeRoots.map((r) => r.box),
        this.qmDecoder
      )
    );
  }

  public override getGeometry(_ids?: number[]): Geometry[][] {
    const result: Geometry[][] = [];
    this.root.traverse((obj) => {
      if (obj && obj instanceof Mesh) {
        result.push([
          {
            transform: obj.matrix.clone(),
            type: GEOMETRY_TYPE_MESH as GeometryType,
            indices: obj.geometry.index?.array as ArrayLike<number>,
            positions: obj.geometry.attributes.position
              .array as ArrayLike<number>,
            normals: obj.geometry.attributes.normal?.array as ArrayLike<number>,
          },
        ]);
      }
    });
    return result;
  }
}
